import { ConfigProvider } from 'shared/config/ConfigContext';
import carrentalData from 'shared/seo/data/carrental.json';
import { LinkTree } from 'shared/seo/LinkTree';
import { PopularCities } from 'shared/seo/PopularCities';
import { PopularCountries } from 'shared/seo/PopularCountries';
import { DestinationPage } from '@/components/DestinationPage';
import { carRentalSearchFactory, countryLevel1Factory } from '@/components/factory';
import { feConfig } from '@/config';
import { carrentalCountries, carrentalMapping, getCountriesForDestination } from '@/data';
import { verticals } from '@/data/verticals';
import type { CountryPageProps, SeoPageContext } from '@/interfaces/pages';

export const getStaticPaths = async () => {
    const destinations = carrentalCountries.map((place) => ({
        params: { countryLevel1: place.countryPath },
    }));

    return {
        paths: destinations,
        fallback: false,
    };
};

export const getStaticProps = async (context: SeoPageContext) => {
    const destination = carrentalCountries.find((place) => place.countryPath === context.params.countryLevel1.toLowerCase());

    const popularCities = (carrentalMapping[destination.id] ?? []).slice(0, 11);
    const popularCountries = getCountriesForDestination(destination, carrentalCountries);

    return {
        props: {
            destination,
            popularCities,
            popularCountries,
            config: feConfig,
        },
    };
};

const CountryLevel1 = ({ destination, popularCities, popularCountries, config }: CountryPageProps) => {
    const factory = countryLevel1Factory(destination, carRentalSearchFactory());

    return (
        <ConfigProvider config={config}>
            <DestinationPage factory={factory} vertical={verticals.carrental} data={carrentalData}>
                <PopularCities
                    destination={destination}
                    cities={popularCities}
                    cdnUrl={carrentalData.cdnPath}
                    verticalUrl={carrentalData.basePath}
                    lang={carrentalData.lang}
                />
                <PopularCountries
                    destination={destination}
                    countries={popularCountries}
                    cdnUrl={carrentalData.cdnPath}
                    verticalUrl={carrentalData.basePath}
                    lang={carrentalData.lang}
                />
                <LinkTree
                    heading={carrentalData.lang.linkTree.heading}
                    description={carrentalData.lang.linkTree.description}
                    data={carrentalData.linkTree}
                    url={carrentalData.basePath}
                />
            </DestinationPage>
        </ConfigProvider>
    );
};

export default CountryLevel1;
